<template>
    <div class="flex-fill d-flex gap-2 flex-column w-100">
        <InputSelect
            :datasource="internalDatasource"
            v-model="value"
            :state="LoadState"
            :validator="props.validator"
            :disabled="!editable"
            ref="inputref"
        />

        <!-- This is a warning if you dont have a profile, to make one first 
         TODO: This is not implemented yet. Here we sould call the generateProfile method -->
        <AlertMessage
            v-if="internalDatasource.source?.length === 0"
            type="warning"
            size="sm"
        >
            <i18next :translation="$t('profile.missingprofile')">
                <template #action>
                    <a
                        href="#"
                        @click="
                            () => {
                                useDialog().Ok({
                                    title: $tm('profile.profile'),
                                    body: $tm('profile.create_profile_must_be_done_in_admin'),
                                    options: { Size: 'md' },
                                });
                            }
                        "
                        >{{ $tm('general.click') }}</a
                    >
                </template>
            </i18next>
        </AlertMessage>
    </div>
</template>
<script lang="ts" setup>
import { useDialog, useTaskLoader, useValidator } from '@/composeables';
import type { SelectLine } from '@/objects';
import type { IEditorComponent, ValidatorDef } from '@/objects/gui';
import { AlertMessage, InputSelect } from '@/components';
import { watch, ref, type ComponentPublicInstance } from 'vue';
import type { EventinfoBase } from '@/objects/generated';
import { ProfileDao } from '@/dao';
import type { ISelectLineDatasource } from '@/objects/ISelectLineDatasource';

defineOptions({ inheritAttrs: false });

/**
 * We define the model to make sure the value is bound to the input
 * This is the actual ProfileID
 */
const value = defineModel<number | null>({ required: true });

const props = withDefaults(
    defineProps<{
        validator?: ValidatorDef | null;
        row: EventinfoBase;

        editable?: boolean;
    }>(),
    {
        validator: null,
        editable: true,
    },
);

/**
 * We define the emits to notify the parent component when the profile count is loaded
 */
const emits = defineEmits<{
    (e: 'loadedprofilecount', value: number): void;
}>();

/**
 * The input reference
 */
const inputref = ref<ComponentPublicInstance<IEditorComponent> | null>(null);

/**
 * We need to load the federations for the organizer and pass the state to the input so we can show a loading spinner
 */
const { AddLoader, LoadState } = useTaskLoader();

/**
 * The internal datasource
 */

const internalDatasource = ref<ISelectLineDatasource>({});

/**
 * Load the profiles for the organizer
 * @param organizerId
 */
const loadProfiles = async (organizerId: number) => {
    internalDatasource.value.source = [];

    if (organizerId === 0) {
        return;
    }
    AddLoader(async () => {
        // Load the profiles
        const organizerProfiles = (await ProfileDao.GetProfilesByOrganizer(organizerId)) ?? [];

        internalDatasource.value.source = organizerProfiles.map((o) => ({ text: o.Name, value: o.ID }));

        // If we didnt manage to load the current value, and we are not editable, we need to add a no change option
        if (!props.editable && !organizerProfiles.find((o) => o.ID === value.value)) {
            internalDatasource.value.source.push({
                text: 'general.unchanged',
                value: value.value,
            });
        }

        // If we have a value and that is valid from the profiles we loaded, use that,
        // otherwise use the first profile we found,
        // otherwise fallback to whatever the value already is
        value.value = internalDatasource.value.source.find((o) => o.value === value.value)
            ? value.value
            : organizerProfiles.length > 0
              ? organizerProfiles[0].ID
              : value.value;

        // Emit the loaded profile count
        emits('loadedprofilecount', organizerProfiles.length);
    }, '');
};

const { Validate, ValidateMessage } = useValidator(props.validator, value)!;

/**
 * Watch the row for changes on the OrganizerID
 * When we get a change, we need to reload the datasource
 */
watch(
    () => props.row.OrganizerID,
    (nv) => loadProfiles(nv),
    { immediate: true },
);

/**
 * We revalidate the value when the value changes
 * this should not be neccassary since the validation should be done in the input.
 * But for some reason I cant get TS to just pass through the ref from the input.
 */
watch(
    () => value.value,
    (nv) => Validate(nv ?? 0),
    { immediate: true },
);

defineExpose<IEditorComponent>({
    focus: () => null,
    validateMessage: ValidateMessage,
});
</script>
